import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { startOfDay, addDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";

export interface SamsaraVehicle {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  speed: number;
  heading: string;
  /** Raw bearing in degrees (0 = N, 90 = E). Null when Samsara has no heading. */
  headingDeg: number | null;
  currentCity: string;
  status: "available" | "in_transit" | "in_service";
  lastUpdate: string;
  /** Raw ISO timestamp of last GPS ping from Samsara (for staleness checks). */
  lastUpdateIso?: string | null;
  driver: string | null;
  nextStop: string | null;
  capabilities?: string[];
  odometerMeters: number | null;
  /** Trailer ID text from active in_transit order */
  trailerId?: string | null;
  /** Order unique_id from active in_transit order */
  orderNumber?: string | null;
  /** Order UUID from active in_transit order (for deep-linking) */
  orderId?: string | null;
  /** Base truck make from tractors table */
  baseTruck?: string | null;
}

export function useSamsaraVehicles() {
  const { tenantId, tenantReady } = useTenant();

  return useQuery<SamsaraVehicle[]>({
    queryKey: ["samsara-vehicles", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      // Fetch tractor inventory and Samsara vehicles in parallel
      const [tractorsResult, samsaraResult] = await Promise.all([
        supabase.from("tractors").select("id, name, capabilities, base_truck"),
        supabase.functions.invoke("fetch-samsara-vehicles"),
      ]);

      if (samsaraResult.error) throw samsaraResult.error;

      const tractors = tractorsResult.data ?? [];

      // Build maps: name -> capabilities, name -> tractor uuid
      const tractorCapMap = new Map<string, string[]>();
      const tractorIdMap = new Map<string, string>();
      const tractorBaseTruckMap = new Map<string, string | null>();
      for (const t of tractors) {
        const key = t.name.trim().toLowerCase();
        tractorCapMap.set(key, t.capabilities as string[]);
        tractorIdMap.set(key, t.id);
        tractorBaseTruckMap.set(key, (t as any).base_truck ?? null);
      }

      // Get all tractor UUIDs that match Samsara vehicles
      const matchedTractorIds = (samsaraResult.data.vehicles as SamsaraVehicle[])
        .map((v) => tractorIdMap.get(v.name.trim().toLowerCase()))
        .filter(Boolean) as string[];

      // Fetch active in_transit orders for these tractors.
      // Guard against stale orders that were never marked delivered: require
      // ready_time within a recent rolling window so the live-fleet sidebar
      // only surfaces orders that plausibly match the truck's current trip.
      const orderMap = new Map<
        string,
        { id: string; unique_id: string; trailer_id: string | null; ready_time: string | null }
      >();
      if (matchedTractorIds.length > 0) {
        // Compute today boundaries in LA timezone
        const nowLA = toZonedTime(new Date(), "America/Los_Angeles");
        const todayStart = startOfDay(nowLA).toISOString();
        const tomorrowStart = startOfDay(addDays(nowLA, 1)).toISOString();
        // In-transit orders older than this are considered stale and ignored.
        const inTransitFloor = addDays(nowLA, -2).toISOString();

        const { data: orders } = await supabase
          .from("orders")
          .select("id, assigned_tractor_id, unique_id, trailer_id, status, ready_time")
          .in("assigned_tractor_id", matchedTractorIds)
          .eq("tenant_id", tenantId!)
          .or(
            `and(status.eq.in_transit,ready_time.gte.${inTransitFloor}),and(status.eq.confirmed,ready_time.gte.${todayStart},ready_time.lt.${tomorrowStart})`
          );

        if (orders) {
          // If a tractor matches multiple orders, keep the most recent by ready_time
          // so a forgotten in_transit record never wins over a live one.
          for (const o of orders) {
            if (!o.assigned_tractor_id) continue;
            const existing = orderMap.get(o.assigned_tractor_id);
            if (
              !existing ||
              (o.ready_time &&
                (!existing.ready_time || o.ready_time > existing.ready_time))
            ) {
              orderMap.set(o.assigned_tractor_id, {
                id: o.id,
                unique_id: o.unique_id,
                trailer_id: o.trailer_id,
                ready_time: o.ready_time,
              });
            }
          }
        }
      }

      return (samsaraResult.data.vehicles as SamsaraVehicle[])
        .filter(
          (v) =>
            v.lat !== null &&
            v.lng !== null &&
            tractorCapMap.has(v.name.trim().toLowerCase())
        )
        .map((v) => {
          const key = v.name.trim().toLowerCase();
          const tractorUuid = tractorIdMap.get(key);
          const activeOrder = tractorUuid ? orderMap.get(tractorUuid) : undefined;
          return {
            ...v,
            capabilities: tractorCapMap.get(key) ?? [],
            baseTruck: tractorBaseTruckMap.get(key) ?? null,
            trailerId: activeOrder?.trailer_id ?? null,
            orderNumber: activeOrder?.unique_id ?? null,
            orderId: activeOrder?.id ?? null,
          };
        });
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
}